import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, FlaskConical, Shield, Smartphone, LifeBuoy } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Section = ({ children }) => (
  <div className="text-sm leading-relaxed text-foreground/90 space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_b]:font-semibold">
    {children}
  </div>
);

export default function HelpPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="help-back-btn">
          <ArrowLeft className="h-4 w-4" />Retour
        </button>
        <div className="flex items-center gap-2 mb-2">
          <LifeBuoy className="h-6 w-6 text-primary" />
          <h1 className="text-2xl sm:text-3xl font-bold">Aide &amp; guides</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6">L'essentiel pour utiliser Jeff Connect au quotidien.</p>

        <Accordion type="single" collapsible className="w-full" data-testid="help-accordion">
          <AccordionItem value="cabinet">
            <AccordionTrigger className="text-left" data-testid="help-cabinet-trigger">
              <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" />Cabinets — passer et suivre une commande</span>
            </AccordionTrigger>
            <AccordionContent>
              <Section>
                <ol>
                  <li><b>Nouvelle commande</b> : Commandes → « Nouvelle commande ». Renseignez la référence interne du patient (jamais son nom), le type de travail, la teinte et les dents concernées.</li>
                  <li><b>Fichiers</b> : joignez vos empreintes numériques (STL) et photos cliniques. Sur mobile, le bouton caméra permet de photographier directement ; les images sont compressées automatiquement.</li>
                  <li><b>Suivi</b> : chaque commande affiche son statut en temps réel (Reçue → En fabrication → Expédiée → Livrée). Vous êtes notifié à chaque étape.</li>
                  <li><b>Messagerie</b> : posez vos questions depuis la fiche commande ; les échanges restent liés au dossier.</li>
                  <li><b>Factures</b> : onglet Factures — consultez et téléchargez vos factures en PDF selon votre grille tarifaire.</li>
                </ol>
                <p>Les fichiers d'un dossier clôturé sont conservés <b>90 jours</b> puis supprimés automatiquement (la traçabilité du dossier est conservée).</p>
              </Section>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="lab">
            <AccordionTrigger className="text-left" data-testid="help-lab-trigger">
              <span className="flex items-center gap-2"><FlaskConical className="h-4 w-4 text-emerald-600" />Laboratoires — fabriquer et livrer</span>
            </AccordionTrigger>
            <AccordionContent>
              <Section>
                <ol>
                  <li><b>Dossiers assignés</b> : votre tableau de bord liste les dossiers qui vous sont confiés, avec date limite de fabrication.</li>
                  <li><b>Bon de travail</b> : téléchargez le bon de travail PDF (trilingue, avec QR code) et les fichiers d'empreinte.</li>
                  <li><b>Avancement</b> : mettez à jour le statut à chaque étape ; joignez le <b>certificat de conformité</b> avant expédition (obligatoire).</li>
                  <li><b>Factures fournisseur</b> : déposez vos factures (PDF/Excel) dans l'onglet Factures ; elles sont rapprochées automatiquement des dossiers.</li>
                </ol>
                <p>Les coordonnées des cabinets ne sont jamais visibles : Jeff Connect est l'intermédiaire unique.</p>
              </Section>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="admin">
            <AccordionTrigger className="text-left" data-testid="help-admin-trigger">
              <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-slate-600" />Administration — l'essentiel</span>
            </AccordionTrigger>
            <AccordionContent>
              <Section>
                <ul>
                  <li><b>Cockpit</b> : vue temps réel des dossiers à traiter, retards et alertes.</li>
                  <li><b>Inscriptions</b> : validez ou refusez les demandes de nouveaux cabinets.</li>
                  <li><b>Facturation</b> : génération des factures clients depuis les grilles tarifaires, import des factures fournisseurs, marquage « payée ».</li>
                  <li><b>Associés</b> : répartition automatique des bénéfices et suivi des prélèvements.</li>
                  <li><b>Sauvegardes</b> : sauvegarde quotidienne automatique à 2 h 30, déclenchement manuel et test de restauration depuis le module Sauvegardes.</li>
                  <li><b>Rétention</b> : purge automatique des fichiers à 90 jours, paramétrable.</li>
                </ul>
                <p>La documentation complète (guide utilisateur ~10 pages) est disponible auprès de l'administrateur : <code className="text-xs bg-muted px-1 rounded">GUIDE_UTILISATEUR.md</code>.</p>
              </Section>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="pwa">
            <AccordionTrigger className="text-left" data-testid="help-pwa-trigger">
              <span className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-blue-600" />Installer l'application sur mobile (PWA)</span>
            </AccordionTrigger>
            <AccordionContent>
              <Section>
                <p><b>iPhone / iPad (Safari)</b></p>
                <ol>
                  <li>Ouvrez Jeff Connect dans <b>Safari</b>.</li>
                  <li>Touchez le bouton <b>Partager</b> (carré avec flèche vers le haut).</li>
                  <li>Choisissez <b>« Sur l'écran d'accueil »</b> puis <b>Ajouter</b>.</li>
                </ol>
                <p><b>Android (Chrome)</b></p>
                <ol>
                  <li>Ouvrez Jeff Connect dans <b>Chrome</b>.</li>
                  <li>Touchez le menu <b>⋮</b> en haut à droite.</li>
                  <li>Choisissez <b>« Installer l'application »</b> (ou « Ajouter à l'écran d'accueil ») puis confirmez.</li>
                </ol>
                <p>L'application s'ouvre alors en plein écran, comme une application native. Activez les <b>notifications</b> depuis la cloche 🔔 pour être alerté des nouveaux messages et changements de statut.</p>
              </Section>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="support">
            <AccordionTrigger className="text-left" data-testid="help-support-trigger">
              <span className="flex items-center gap-2"><LifeBuoy className="h-4 w-4 text-amber-600" />Problème de connexion &amp; support</span>
            </AccordionTrigger>
            <AccordionContent>
              <Section>
                <ul>
                  <li><b>Mot de passe oublié</b> : sur la page de connexion, cliquez « Mot de passe oublié ? » et saisissez votre email.</li>
                  <li><b>Compte en attente</b> : les inscriptions de cabinets sont validées sous 48 h ouvrées.</li>
                  <li><b>Support</b> : jeffpdental — +1 505 301 1071.</li>
                </ul>
              </Section>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-8 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground">
          <Link to="/mentions-legales" className="hover:underline" data-testid="help-link-mentions">Mentions légales</Link>
          <Link to="/cgu" className="hover:underline" data-testid="help-link-cgu">CGU</Link>
          <Link to="/confidentialite" className="hover:underline" data-testid="help-link-confidentialite">Confidentialité</Link>
        </div>
      </div>
    </div>
  );
}
